import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../util/api";
import { useHistory } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import dayjs from "dayjs";

import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    CircularProgress,
    Backdrop,
    Card,
    CardHeader,
    CardContent,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Button,
    Stack,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Paper,
    LinearProgress,
    Chip,
    Alert,
    useMediaQuery,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";
import LoadingDots from "../../component/common/LoadingDots";
import ActionButton from "../../component/common/ActionButton";
import ResizableTable from "../../component/common/ResizableTable";
import { notify } from "../../util/toast";

import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PaidIcon from "@mui/icons-material/Paid";
import ApartmentIcon from "@mui/icons-material/Apartment";
import PersonIcon from "@mui/icons-material/Person";
import LaunchIcon from "@mui/icons-material/Launch";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import NumbersIcon from "@mui/icons-material/Numbers";
import GavelIcon from "@mui/icons-material/Gavel";
import RuleIcon from "@mui/icons-material/Rule";
import LinkIcon from "@mui/icons-material/Link";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { ds } from "../../styles/designSystem";
import { useMyPermissions } from "../../util/useMyPermissions";

/**
 * 8개 영역 × 35+ 필드. 각 필드는 객체로 표현.
 * - label: 표시명
 * - key  : DB(BidAnnouncement) 의 필드명 (JSON camelCase)
 * - apiKey: API(BidAnnounceDetailResponse) 의 필드명 (다를 때만 명시. 기본=key)
 * - kind  : "money" | "rate" | "yn" — 표시 포맷팅용 (기본=문자열 그대로)
 */
// AI 분석 응답 중 `[출처: 파일명/페이지/줄]` 패턴을 회색 작은 span 으로 분리 표시.
//   AI 가 본문에서 인용한 출처를 일관된 형식으로 출력 → 본문은 그대로 두고 출처만 별도 스타일.
//   text node 안에서만 split — 다른 inline element (strong, em 등) 는 그대로 통과.
//   캡처 그룹이 있는 split 이므로 split 결과 자체에 매칭 부분도 포함됨 (홀수 인덱스 = 매칭).
const CITE_SPLIT = /(\[출처:[^\]]+\])/g;
const renderCitations = (children) => {
    return React.Children.map(children, (child, idx) => {
        if (typeof child !== "string") return child;
        const parts = child.split(CITE_SPLIT);
        if (parts.length === 1) return child;
        return parts.map((p, i) =>
            p.startsWith("[출처:") ? (
                <Box
                    key={`cite-${idx}-${i}`}
                    component="span"
                    sx={{
                        display: "inline",
                        color: "#94a3b8",
                        fontSize: "0.78em",
                        fontWeight: 400,
                        ml: 0.5,
                        whiteSpace: "nowrap",
                    }}
                >
                    {p}
                </Box>
            ) : (
                <React.Fragment key={`txt-${idx}-${i}`}>{p}</React.Fragment>
            )
        );
    });
};

// ReactMarkdown 의 components 매핑 — text 가 들어오는 inline-bearing element 들을 모두 wrap.
const aiMarkdownComponents = {
    p:  ({ node, children, ...rest }) => <p  {...rest}>{renderCitations(children)}</p>,
    li: ({ node, children, ...rest }) => <li {...rest}>{renderCitations(children)}</li>,
    td: ({ node, children, ...rest }) => <td {...rest}>{renderCitations(children)}</td>,
    th: ({ node, children, ...rest }) => <th {...rest}>{renderCitations(children)}</th>,
};

const CATEGORY_FIELDS = {
    "식별": [
        { label: "공고번호", key: "bidNoticeNumber", apiKey: "bidNtceNo" },
        { label: "공고차수", key: "bidNoticeOrder",  apiKey: "bidNtceOrd" },
        { label: "공고명",   key: "bidNtceNm" },
        { label: "참조번호", key: "refNo" },
    ],
    "공고 일반": [
        { label: "공고종류",     key: "ntceKindNm" },
        { label: "재공고 여부",  key: "reNtceYn",  kind: "yn" },
        { label: "등록유형",     key: "rgstTyNm" },
        { label: "국제입찰여부", key: "intrbidYn", kind: "yn" },
        { label: "변경공고 사유", key: "chgNtceRsn" },
    ],
    "입찰일정": [
        { label: "입찰공고일시",   key: "bidNtceDt" },
        { label: "등록일시",       key: "rgstDt" },
        { label: "공고 변경일시",  key: "chgDt" },
        { label: "참가자격 등록", key: "bidQlfctRgstDt" },
        { label: "입찰개시",       key: "bidBeginDt" },
        { label: "입찰마감",       key: "bidClseDt" },
        { label: "입찰보증금 마감", key: "bidWgrnteeRcptClseDt" },
        { label: "개찰일시",       key: "opengDt" },
        { label: "재입찰 개찰",    key: "rbidOpengDt" },
    ],
    "계약 / 낙찰 방식": [
        { label: "입찰방식",       key: "bidMethdNm" },
        { label: "계약체결방법",   key: "cntrctCnclsMthdNm" },
        { label: "낙찰자결정코드", key: "sucsfbidMthdCd" },
        { label: "낙찰자결정방법", key: "sucsfbidMthdNm" },
        { label: "낙찰하한율",     key: "sucsfbidLwltRate", kind: "rate" },
        { label: "예가결정방법",   key: "prearngPrceDcsnMthdNm" },
        { label: "용역구분",       key: "srvceDivNm" },
    ],
    "예산 및 가격": [
        { label: "배정예산",       key: "asignBdgtAmt",  kind: "money" },
        { label: "추정가격",       key: "presmptPrce",   kind: "money" },
        { label: "부가가치세",     key: "vat", apiKey: "VAT", kind: "money" },
        { label: "입찰참가수수료", key: "bidPrtcptFee",  kind: "money" },
    ],
    "기관 및 담당자": [
        { label: "공고기관",   key: "ntceInsttNm" },
        { label: "공고기관코드", key: "ntceInsttCd" },
        { label: "수요기관",   key: "dminsttNm" },
        { label: "수요기관코드", key: "dminsttCd" },
        { label: "담당자",     key: "ntceInsttOfclNm" },
        { label: "연락처",     key: "ntceInsttOfclTelNo" },
        { label: "이메일",     key: "ntceInsttOfclEmailAdrs" },
    ],
    "참가제한 / 옵션": [
        { label: "지역제한 (공동수급)", key: "cmmnSpldmdCorpRgnLmtYn", kind: "yn" },
        { label: "참가제한 여부",       key: "bidPrtcptLmtYn",        kind: "yn" },
        { label: "재입찰 허용",         key: "rbidPermsnYn",          kind: "yn" },
        { label: "내역입찰 여부",       key: "dtlsBidYn",             kind: "yn" },
        { label: "PQ 심사",            key: "pqEvalYn",              kind: "yn" },
        { label: "TP 심사",            key: "tpEvalYn",              kind: "yn" },
    ],
    "URL / 시스템": [
        { label: "공고 URL",        key: "bidNtceUrl" },
        { label: "상세 URL",        key: "bidNtceDtlUrl" },
        { label: "개찰 처리 완료",  key: "bidOpenResult",         kind: "yn" },
        { label: "개찰 결과 없음",  key: "bidOpenResultEmptyData", kind: "yn" },
    ],
};

const categoryIcon = (title) => {
    switch (title) {
        case "식별":             return <NumbersIcon fontSize="small" />;
        case "공고 일반":         return <DescriptionIcon fontSize="small" />;
        case "입찰일정":          return <CalendarMonthIcon fontSize="small" />;
        case "계약 / 낙찰 방식":   return <GavelIcon fontSize="small" />;
        case "예산 및 가격":      return <PaidIcon fontSize="small" />;
        case "기관 및 담당자":    return <ApartmentIcon fontSize="small" />;
        case "참가제한 / 옵션":   return <RuleIcon fontSize="small" />;
        case "URL / 시스템":      return <LinkIcon fontSize="small" />;
        default:                 return <PersonIcon fontSize="small" />;
    }
};

const formatValue = (kind, value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (kind === "money") {
        const num = Number(value);
        if (Number.isFinite(num)) return `₩ ${new Intl.NumberFormat().format(num)}`;
        return String(value);
    }
    if (kind === "rate") {
        const num = Number(value);
        if (Number.isFinite(num)) return `${num.toFixed(3)} %`;
        return String(value);
    }
    if (kind === "yn") {
        const s = String(value).toUpperCase();
        if (s === "Y" || s === "TRUE") return "Y";
        if (s === "N" || s === "FALSE") return "N";
        return String(value);
    }
    return String(value);
};

/**
 * 값 정규화 — boolean ↔ "Y/N" / "TRUE/FALSE" 같은 표현 차이 흡수.
 * DB(boolean) 와 API(Y/N 문자열) 가 의미적으론 같지만 표면 형태가 달라서
 * diff 비교 시 false-positive 가 생기던 문제 차단.
 */
const normalizeValue = (v) => {
    if (v === null || v === undefined || v === "") return "";
    if (v === true) return "Y";
    if (v === false) return "N";
    if (typeof v === "string") {
        const u = v.trim().toUpperCase();
        if (u === "TRUE" || u === "Y") return "Y";
        if (u === "FALSE" || u === "N") return "N";
        return v;
    }
    return v;
};

/** 두 값이 의미상 같은지 비교 (boolean/Y-N/날짜/숫자/문자열 형식 차이 흡수). */
const valuesEqual = (a, b) => {
    const na = normalizeValue(a);
    const nb = normalizeValue(b);
    const aEmpty = na === "" || na === null || na === undefined;
    const bEmpty = nb === "" || nb === null || nb === undefined;
    if (aEmpty && bEmpty) return true;
    if (aEmpty || bEmpty) return false;
    // 정규화 후 같으면 같음 (Y/N, boolean 케이스)
    if (na === nb) return true;
    // 숫자 비교 — 둘 다 숫자로 파싱 가능하면 숫자로 비교
    if (!isNaN(Number(na)) && !isNaN(Number(nb))) {
        return Number(na) === Number(nb);
    }
    // 날짜 표현 차이 흡수 ("2025-07-01 13:21:14" vs "2025-07-01T13:21:14")
    return String(na).replace("T", " ").trim() === String(nb).replace("T", " ").trim();
};

/**
 * 셀 1개의 표시 상태 계산.
 * @param dbValue  우리 DB 의 값 (BidAnnouncement)
 * @param apiValue 외부 API 응답 값 (BidAnnounceDetailResponse)
 * @returns {{display, diffState}} diffState: "same" | "new" | "changed" | "removed"
 */
const computeDiff = (dbValue, apiValue, apiLoaded) => {
    // 외부 API 응답이 아직 안 옴 — DB 값만 표시
    if (!apiLoaded) {
        return { display: dbValue, diffState: "same", oldValue: null };
    }
    if (valuesEqual(dbValue, apiValue)) {
        return { display: apiValue, diffState: "same", oldValue: null };
    }
    const dbEmpty = dbValue === null || dbValue === undefined || dbValue === "";
    if (dbEmpty && apiValue) {
        // DB 에 없던 신규 값 — 노란색
        return { display: apiValue, diffState: "new", oldValue: null };
    }
    if (dbValue && (apiValue === null || apiValue === undefined || apiValue === "")) {
        // API 에서 값이 사라짐 (취소/삭제) — 옛 값 취소선
        return { display: dbValue, diffState: "removed", oldValue: null };
    }
    // 값이 변경됨 — 옛 값 취소선 + 새 값
    return { display: apiValue, diffState: "changed", oldValue: dbValue };
};

/** diffState 별 셀 스타일. */
const diffStyle = (diffState) => {
    switch (diffState) {
        case "new":     return { }; // 노란색
        case "changed": return { };
        case "removed": return { bgcolor: "#fee2e2", borderLeft: "3px solid #dc2626" };
        default:        return {};
    }
};

/** diff 셀 컴포넌트 — 신규/변경/제거 시각화. */
function DiffCell({ kind, dbValue, apiValue, apiLoaded }) {
    const { display, diffState, oldValue } = computeDiff(dbValue, apiValue, apiLoaded);
    const style = diffStyle(diffState);


    if (diffState === "new" || diffState === "changed") {
        return (
            <>
                {formatValue(kind, display)}
            </>

        );
    }

    if (diffState === "removed") {
        return (
            <Box sx={{ ...style, px: 1, py: 0.6, borderRadius: 0 }}>
                <Stack direction="row" alignItems="baseline" spacing={0.8}>
                    <Box sx={{
                        fontSize: 9.5, fontWeight: 800, letterSpacing: "0.02em",
                        color: "#fff", bgcolor: "#dc2626",
                        px: 0.55, py: 0.05, borderRadius: 0,
                    }}>
                        제거
                    </Box>
                    <Typography sx={{
                        textDecoration: "line-through",
                        color: "#9ca3af",
                        wordBreak: "break-word",
                        fontSize: 12.5,
                    }}>
                        {formatValue(kind, display)}
                    </Typography>
                </Stack>
            </Box>
        );
    }

    // same
    return (
        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
            {formatValue(kind, display)}
        </Typography>
    );
}

function CategoryCard({ title, fields, dbData, apiData, apiLoaded }) {
    return (
        <Card
            variant="outlined"
            sx={{
                width: "100%",
                ...ds.components.external.card,
                overflow: "hidden",
                borderRadius: 0,
            }}
        >
            <CardHeader
                avatar={categoryIcon(title)}
                title={<Typography sx={ds.typography.sectionTitle}>{title}</Typography>}
                sx={{
                    py: 1.25,
                    bgcolor: ds.colors.bg,
                    borderBottom: `1px solid ${ds.colors.borderLight}`,
                }}
            />
            <CardContent sx={{ pt: 0 }}>
                <ResizableTable size="small">
                    <TableBody>
                        {fields.map((f) => {
                            const { label, key, apiKey, kind } = f;
                            const dbValue = dbData?.[key];
                            const apiValue = apiData?.[apiKey ?? key];
                            const detailUrl = apiData?.bidNtceDtlUrl ?? dbData?.bidNtceDtlUrl;
                            return (
                                <TableRow
                                    key={key}
                                    hover
                                    sx={{
                                        "& td": { borderBottom: `1px solid ${ds.colors.borderLight}` },
                                        "&:last-child td": { borderBottom: "none" },
                                    }}
                                >
                                    <TableCell
                                        sx={{
                                            width: 160,
                                            bgcolor: ds.colors.bg,
                                            fontWeight: 700,
                                            color: "text.secondary",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {label}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 0 }}>
                                        {label === "공고명" && detailUrl ? (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <DiffCell kind={kind} dbValue={dbValue} apiValue={apiValue} apiLoaded={apiLoaded} />
                                                </Box>
                                                <ActionButton
                                                    variant="secondary"
                                                    startIcon={false}
                                                    component="a"
                                                    href={detailUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{ fontWeight: 850, flexShrink: 0, borderColor: "transparent",
                                                        "&:hover": { borderColor: "transparent", bgcolor: ds.colors.primaryBg } }}
                                                    label={<Stack direction="row" alignItems="center" spacing={0.5}><span>원문</span><LaunchIcon sx={{ fontSize: 14 }} /></Stack>}
                                                />
                                            </Box>
                                        ) : (
                                            <DiffCell kind={kind} dbValue={dbValue} apiValue={apiValue} apiLoaded={apiLoaded} />
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </ResizableTable>
            </CardContent>
        </Card>
    );
}

/**
 * 첨부파일 카드 — DB 우선 표시, API 와 비교해 신규/변경/제거 강조.
 * - DB.attachments: [{seq, fileName, fileUrl}, ...] (이미 정렬됨)
 * - API: ntceSpecDocUrl{1..10} / ntceSpecFileNm{1..10}
 */
function AttachmentsCard({ dbInfo, apiInfo, apiLoaded }) {
    const dbList = Array.isArray(dbInfo?.attachments) ? dbInfo.attachments : [];

    // API → seq 키 맵
    const apiBySeq = {};
    if (apiLoaded && apiInfo) {
        for (let i = 1; i <= 10; i++) {
            const url = apiInfo[`ntceSpecDocUrl${i}`];
            const name = apiInfo[`ntceSpecFileNm${i}`];
            if (url) apiBySeq[i] = { seq: i, fileName: name, fileUrl: url };
        }
    }

    // 통합 — seq 합집합. 각 항목의 diff 상태 계산.
    const allSeqs = new Set([...dbList.map(a => a.seq), ...Object.keys(apiBySeq).map(Number)]);
    const merged = [...allSeqs].sort((a, b) => a - b).map((seq) => {
        const db = dbList.find(a => a.seq === seq);
        const api = apiBySeq[seq];
        if (db && !api) return { ...db, state: apiLoaded ? "removed" : "same" };
        if (!db && api) return { ...api, state: "new" };
        if (db && api && (db.fileUrl !== api.fileUrl || db.fileName !== api.fileName)) {
            return { ...api, state: "changed", oldFileName: db.fileName, oldFileUrl: db.fileUrl };
        }
        return { ...(api || db), state: "same" };
    });

    return (
        <Card variant="outlined" sx={{ ...ds.components.external.card, borderRadius: 0 }}>
            <CardHeader
                avatar={<DescriptionIcon fontSize="small" />}
                title={<Typography sx={ds.typography.sectionTitle}>
                    첨부파일 ({merged.length})
                </Typography>}
                sx={{
                    py: 1.25,
                    bgcolor: ds.colors.bg,
                    borderBottom: `1px solid ${ds.colors.borderLight}`,
                }}
            />
            <CardContent>
                {merged.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        첨부파일이 없습니다.
                    </Typography>
                ) : (
                    <List dense>
                        {merged.map((a) => {
                            const stateStyle = a.state === "new"     ? { bg: "#fef9c3", color: "#854d0e", tag: "신규" }
                                            : a.state === "changed" ? { bg: "#fef3c7", color: "#92400e", tag: "변경" }
                                            : a.state === "removed" ? { bg: "#fee2e2", color: "#dc2626", tag: "제거" }
                                            : null;
                            return (
                                <ListItem key={a.seq} disableGutters
                                    sx={stateStyle ? { bgcolor: stateStyle.bg, borderRadius: 1, mb: 0.5, px: 1 } : {}}>
                                    <ListItemIcon sx={{ minWidth: 34 }}>
                                        <DescriptionIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <ActionButton
                                                    variant="secondary"
                                                    startIcon={false}
                                                    component="a"
                                                    href={a.fileUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        justifyContent: "flex-start",
                                                        px: 0,
                                                        fontWeight: 850,
                                                        borderColor: "transparent",
                                                        textDecoration: a.state === "removed" ? "line-through" : "none",
                                                        color: a.state === "removed" ? "#9ca3af" : undefined,
                                                        "&:hover": { borderColor: "transparent", bgcolor: "transparent" },
                                                    }}
                                                    label={<Stack direction="row" alignItems="center" spacing={0.5}><span>{a.fileName || `첨부파일 ${a.seq}`}</span><LaunchIcon sx={{ fontSize: 14 }} /></Stack>}
                                                />
                                                {stateStyle && (
                                                    <Box sx={{ fontSize: 10, fontWeight: 700, color: stateStyle.color,
                                                               px: 0.7, py: 0.2, borderRadius: 0.5, border: `1px solid ${stateStyle.color}` }}>
                                                        {stateStyle.tag}
                                                    </Box>
                                                )}
                                            </Stack>
                                        }
                                        secondary={a.state === "changed" && a.oldFileName ? (
                                            <Typography variant="caption" sx={{ textDecoration: "line-through", color: "#9ca3af" }}>
                                                이전: {a.oldFileName}
                                            </Typography>
                                        ) : null}
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </CardContent>
        </Card>
    );
}

function SimilarBidCardList({ data, loading, onClickBid }) {
    const list = data || [];
    // 낙찰 결과가 있는 카드를 위로 — 정보 풍부한 것 먼저
    const sorted = [...list].sort((a, b) => {
        const aw = a?.bidResultWinnerCompanyInfo?.winnerCompanyName ? 1 : 0;
        const bw = b?.bidResultWinnerCompanyInfo?.winnerCompanyName ? 1 : 0;
        return bw - aw;
    });
    const withWinnerCount = list.filter(i => i?.bidResultWinnerCompanyInfo?.winnerCompanyName).length;

    return (
        <Paper
            variant="outlined"
            sx={{
                width: "100%",
                p: 0,
                borderRadius: 0,
                borderColor: ds.colors.borderLight,
            }}
        >
            {/* 헤더 — 토스 느낌 회색 바 */}
            <Box sx={{
                px: 2, py: 1.25,
                borderBottom: `1px solid ${ds.colors.borderLight}`,
                bgcolor: ds.colors.bg,
                display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
                <Box>
                    <Typography sx={{ ...ds.typography.sectionTitle, fontSize: 14, fontWeight: 800 }}>
                        유사공고 · 낙찰 이력
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        총 {list.length}건{withWinnerCount > 0 ? ` · 낙찰 ${withWinnerCount}건 확인` : ""}
                    </Typography>
                </Box>
                {loading && <CircularProgress size={16} />}
            </Box>

            <Box sx={{ position: "relative", p: 1.25 }}>
                {loading && list.length === 0 && (
                    <Box sx={{
                        py: 4, display: "flex", justifyContent: "center",
                    }}>
                        <LoadingDots label="유사 공고를 불러오고 있어요" />
                    </Box>
                )}

                {!loading && list.length === 0 && (
                    <Typography sx={{ py: 3, textAlign: "center", color: ds.colors.textMuted, fontSize: 12.5 }}>
                        조회된 유사공고가 없습니다.
                    </Typography>
                )}

                <Box sx={{ maxHeight: "calc(80vh - 160px)", overflowY: "auto" }}>
                    <Stack spacing={0.75}>
                        {sorted.map((item) => {
                            const winner = item?.bidResultWinnerCompanyInfo;
                            const hasWinner = !!winner?.winnerCompanyName;
                            return (
                                <Box
                                    key={`${item.bizNo}-${item.bizOrd}`}
                                    onClick={() => onClickBid?.(item)}
                                    sx={{
                                        p: 1.25,
                                        cursor: "pointer",
                                        borderRadius: 0,
                                        border: `1px solid ${ds.colors.borderLight}`,
                                        borderLeft: hasWinner ? `3px solid ${ds.colors.primary}` : `3px solid ${ds.colors.borderLight}`,
                                        bgcolor: "#fff",
                                        transition: "background 0.12s",
                                        "&:hover": { bgcolor: ds.colors.bg },
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 800, fontSize: 13.5, lineHeight: 1.3 }}>
                                        {item.bizNm && item.bizNm.length > 50 ? item.bizNm.slice(0, 50) + "…" : item.bizNm}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, color: ds.colors.textMuted, fontFamily: "monospace", mt: 0.3 }}>
                                        {item.bizNo}-{item.bizOrd}
                                    </Typography>

                                    {hasWinner && (
                                        <Stack spacing={0.3} sx={{ mt: 0.8, fontSize: 12 }}>
                                            {winner.crawling && (
                                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                                    <Box sx={{
                                                        px: 0.7, py: 0.1,
                                                        fontSize: 10, fontWeight: 800, color: "#fff",
                                                        bgcolor: "#dc2626", borderRadius: 0,
                                                    }}>
                                                        개찰 1순위
                                                    </Box>
                                                    <Tooltip title="나라장터 개찰 결과 1순위 기업 (일부 건은 금액 비공개)">
                                                        <HelpOutlineIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                                                    </Tooltip>
                                                </Stack>
                                            )}
                                            <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap" }}>
                                                <Typography sx={{ fontSize: 12 }}>
                                                    <Box component="span" sx={{ color: ds.colors.textMuted, mr: 0.5 }}>낙찰사</Box>
                                                    <Box component="span" sx={{ fontWeight: 800, color: "#dc2626" }}>
                                                        {winner.winnerCompanyName || "-"}
                                                    </Box>
                                                </Typography>
                                                <Typography sx={{ fontSize: 12 }}>
                                                    <Box component="span" sx={{ color: ds.colors.textMuted, mr: 0.5 }}>금액</Box>
                                                    <Box component="span" sx={{ fontWeight: 700, color: ds.colors.primary }}>
                                                        ₩{winner.bidAmount?.toLocaleString() || "-"}
                                                    </Box>
                                                </Typography>
                                                <Typography sx={{ fontSize: 12 }}>
                                                    <Box component="span" sx={{ color: ds.colors.textMuted, mr: 0.5 }}>일자</Box>
                                                    <Box component="span" sx={{ fontWeight: 600 }}>
                                                        {winner.fnlSucsfDate || "-"}
                                                    </Box>
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    )}
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            </Box>
        </Paper>
    );
}

function SelectMoveDialog({ open, onClose, bid, onBid, onCompany }) {
    const companyDisabled = !bid?.bidResultWinnerCompanyInfo?.winnerCompanyBizNumber;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={ds.typography.sectionTitle}>이동 선택</DialogTitle>
            <DialogContent>
                <Stack spacing={1.5} sx={{ pt: 1 }}>
                    <ActionButton variant="primary" onClick={onBid} label="해당 공고 상세로 이동" />
                    <ActionButton variant="secondary" onClick={onCompany} disabled={companyDisabled}
                        label="낙찰 사업자 상세로 이동" />
                    <ActionButton variant="cancel" onClick={onClose} />
                </Stack>
            </DialogContent>
        </Dialog>
    );
}

/**
 * 입찰 일정 타임라인 카드 — 산업의역군 스타일 수직 타임라인.
 * 단계별 도트 + 좌측 세로 라인 + 우측에 라벨/일시/상태.
 * 현재 시각을 기준으로 "완료/현재/예정" 색상 구분.
 */
function ScheduleTimelineCard({ dbInfo }) {
    const now = dayjs();
    const stages = [
        { label: "공고 등록",   key: "rgstDt",    color: "#0ea5e9" },
        { label: "입찰공고",   key: "bidNtceDt", color: "#2563eb" },
        { label: "입찰개시",   key: "bidBeginDt", color: "#6366f1" },
        { label: "입찰마감",   key: "bidClseDt", color: "#dc2626" },
        { label: "개찰일시",   key: "opengDt",   color: "#16a34a" },
    ];

    // 각 단계의 상태 — past(지남) / current(임박, 24h 이내) / future(예정) / unknown(데이터 없음)
    const items = stages.map(s => {
        const iso = dbInfo?.[s.key];
        if (!iso) return { ...s, state: "unknown", at: null };
        const t = dayjs(iso);
        if (!t.isValid()) return { ...s, state: "unknown", at: null };
        if (now.isAfter(t)) return { ...s, state: "past",    at: t };
        if (t.diff(now, "hour") <= 24) return { ...s, state: "current", at: t };
        return { ...s, state: "future", at: t };
    });

    return (
        <Card variant="outlined" sx={{
            mb: 1.5, borderRadius: 0,
            borderColor: ds.colors.borderLight,
        }}>
            <CardHeader
                avatar={<CalendarMonthIcon fontSize="small" />}
                title={<Typography sx={ds.typography.sectionTitle}>진행 일정</Typography>}
                sx={{ py: 1, bgcolor: ds.colors.bg,
                      borderBottom: `1px solid ${ds.colors.borderLight}` }}
            />
            <CardContent sx={{ py: 1.5 }}>
                <Box sx={{ position: "relative", pl: 3 }}>
                    {/* 좌측 세로 라인 */}
                    <Box sx={{
                        position: "absolute", left: 9, top: 6, bottom: 6,
                        width: 2, bgcolor: ds.colors.borderLight,
                    }} />
                    {items.map((it, idx) => {
                        const dotBg = it.state === "past"    ? it.color :
                                      it.state === "current" ? "#fff"  :
                                      it.state === "unknown" ? ds.colors.borderLight : "#fff";
                        const dotBorder = it.state === "current" ? `2px solid ${it.color}` :
                                          it.state === "future"  ? `2px solid ${ds.colors.border}` :
                                          "none";
                        return (
                            <Box key={it.key} sx={{
                                position: "relative", py: 0.75,
                                pl: 0, display: "flex", alignItems: "center", gap: 1.5,
                            }}>
                                <Box sx={{
                                    position: "absolute", left: -19, top: "50%",
                                    transform: "translateY(-50%)",
                                    width: 12, height: 12,
                                    bgcolor: dotBg, border: dotBorder,
                                    borderRadius: 0, // 사각형 도트
                                }} />
                                <Box sx={{ minWidth: 80 }}>
                                    <Typography sx={{
                                        fontSize: 12.5,
                                        fontWeight: it.state === "current" ? 800 : 600,
                                        color: it.state === "unknown" ? ds.colors.textMuted :
                                               it.state === "past" ? ds.colors.textPrimary :
                                               it.color,
                                    }}>
                                        {it.label}
                                    </Typography>
                                </Box>
                                <Typography sx={{
                                    fontSize: 12, color: ds.colors.textMuted,
                                    fontFamily: "monospace",
                                }}>
                                    {it.at ? it.at.format("YYYY-MM-DD HH:mm") : "—"}
                                </Typography>
                                {it.state === "current" && (
                                    <Box sx={{
                                        ml: "auto", px: 0.8, py: 0.1,
                                        fontSize: 10.5, fontWeight: 800, color: "#fff",
                                        bgcolor: it.color, letterSpacing: "0.02em",
                                    }}>
                                        임박
                                    </Box>
                                )}
                                {it.state === "past" && (
                                    <Box sx={{
                                        ml: "auto", px: 0.8, py: 0.1,
                                        fontSize: 10.5, fontWeight: 700, color: "#fff",
                                        bgcolor: "#64748b",
                                    }}>
                                        완료
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </CardContent>
        </Card>
    );
}

/** 입찰 마감일 → "D-N" / "D-day" / "마감". null 안전. */
function computeDday(clseIso) {
    if (!clseIso) return null;
    const now = dayjs();
    const clse = dayjs(clseIso);
    if (!clse.isValid()) return null;
    if (now.isAfter(clse)) return { label: "마감", bg: "#64748b" };
    const days = clse.startOf("day").diff(now.startOf("day"), "day");
    if (days === 0) return { label: "D-day", bg: "#dc2626" };
    if (days <= 3) return { label: `D-${days}`, bg: "#ea580c" };
    return { label: `D-${days}`, bg: "#2563eb" };
}

/**
 * 현재시각 기준 입찰 상태 라벨. 목록(MainSearchPage.getBidStatus)과 동일 기준으로 낙찰자 인지.
 * 개찰완료(bidOpenResult)일 때: 유찰확정→유찰 / 낙찰자 있음→개찰완료 / 그 외→결과 확인필요.
 *
 * @param winnerName 모달에서 별도 조회한 낙찰자명. detail-from-db 는 transient winningCompanyName 을
 *                   채워주지 않으므로(목록 서비스 전용) 호출 측에서 winner 상태를 주입한다.
 */
function computeStatus(item, winnerName) {
    if (!item) return { label: "—", bg: "#e2e8f0", color: "#475569" };
    const now = dayjs();
    if (item.bidOpenResult) {
        // 유찰은 명확한 근거(개찰결과구분 "유찰")로 확정된 경우만.
        if (item.bidFailedConfirmed) return { label: "유찰", bg: "#fee2e2", color: "#b91c1c" };
        // 낙찰자 확인됨 → 개찰완료.
        if (winnerName || item.winningCompanyName) return { label: "개찰완료", bg: "#dcfce7", color: "#16a34a" };
        // 개찰은 됐으나 낙찰자/유찰 근거 미확보 → 유찰로 단정하지 않음.
        return { label: "결과 확인필요", bg: "#f1f5f9", color: "#64748b" };
    }
    const clse = item.bidClseDt ? dayjs(item.bidClseDt) : null;
    const openg = item.opengDt ? dayjs(item.opengDt) : null;
    if (clse && now.isAfter(clse)) {
        if (openg && now.isBefore(openg)) return { label: "개찰대기", bg: "#ede9fe", color: "#7c3aed" };
        return { label: "마감", bg: "#f1f5f9", color: "#64748b" };
    }
    return { label: "입찰전", bg: "#dbeafe", color: "#2563eb" };
}

const BidDetailModal = ({ isOpen, onClose, bidNumber, bidNoticeOrder }) => {
    const history = useHistory();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // 개찰 결과 갱신·AI 분석은 외부 유료 API 를 호출하므로 BID_REFRESH 권한자만.
    // 서버(@RequirePermission)가 최종 방어선이고, 여기서는 실패할 버튼을 감춘다.
    // 공고 조회·검색·상세는 이 권한과 무관하게 전 직원이 그대로 쓴다.
    const { hasPerm } = useMyPermissions();
    const canBidRefresh = hasPerm("BID_REFRESH");

    // DB 데이터: 즉시 표시 (빠른 로드)
    const [dbData, setDbData] = useState(null);
    const [dbLoading, setDbLoading] = useState(false);
    // 외부 API 데이터: 백그라운드 호출 + diff 비교
    const [apiData, setApiData] = useState(null);
    const [apiLoading, setApiLoading] = useState(false);
    const [apiLoaded, setApiLoaded] = useState(false);
    const [apiError, setApiError] = useState(null);

    const [similarBid, setSimilarBid] = useState([]);
    const [similarBidLoading, setSimilarLoading] = useState(false);

    const [selectedSimilar, setSelectedSimilar] = useState(null);
    const [selectModal, setSelectModal] = useState({ open: false, bid: null });

    // ── 낙찰자 ─────────────────────────────────────────────
    const [winner, setWinner] = useState(null);
    const [winnerRefreshing, setWinnerRefreshing] = useState(false);
    // ── 전체 투찰 결과 (개찰완료 공고의 모든 투찰 업체) ──────
    const [bidResults, setBidResults] = useState(null);
    // DB 미저장 공고 — detail-from-db 가 404 면 true. apiData 로 fallback 표시 + 안내 배너.
    const [dbMissing, setDbMissing] = useState(false);

    // ── AI 분석 ────────────────────────────────────────────
    const [aiText, setAiText] = useState("");
    const [aiPhase, setAiPhase] = useState("idle"); // idle | running | done | error
    const [aiError, setAiError] = useState(null);
    const [aiMessage, setAiMessage] = useState(""); // 진행 단계 메시지
    const aiAbortRef = useRef(null);

    useEffect(() => {
        if (!isOpen || !bidNumber || !bidNoticeOrder) return;

        // 상태 초기화
        setDbData(null);
        setApiData(null);
        setApiLoaded(false);
        setApiError(null);
        setSimilarBid([]);
        setWinner(null);
        setBidResults(null);
        setDbMissing(false);
        // AI 분석 상태도 초기화 — 다른 공고 열 때 결과가 남아 있으면 혼란.
        setAiText("");
        setAiPhase("idle");
        setAiError(null);
        setAiMessage("");
        if (aiAbortRef.current) {
            try { aiAbortRef.current.abort(); } catch (_) {}
            aiAbortRef.current = null;
        }

        // 1) DB 우선 — 즉시 표시. 404 면 dbMissing=true 로 표시 + 외부 API fallback.
        setDbLoading(true);
        api.get(`${process.env.REACT_APP_API_BASE_URL}/bids/detail-from-db`,
                { bidNoticeNumber: bidNumber, bidNoticeOrder })
            .then((d) => {
                setDbData(d);
                setDbMissing(false);
                // DB row 가 있고 개찰완료(bidOpenResult=true) 인 경우에만 winner / 전체 투찰 결과 조회.
                if (d?.bidOpenResult === true) {
                    api.get(`${process.env.REACT_APP_API_BASE_URL}/bids/${encodeURIComponent(bidNumber)}/${encodeURIComponent(bidNoticeOrder)}/winner`)
                        .then((w) => setWinner(w && w.winningCompanyName ? w : null))
                        .catch(() => setWinner(null));
                    api.get(`${process.env.REACT_APP_API_BASE_URL}/bids/${encodeURIComponent(bidNumber)}/${encodeURIComponent(bidNoticeOrder)}/bid-results`)
                        .then((r) => setBidResults(r && r.opened ? r : null))
                        .catch(() => setBidResults(null));
                }
            })
            .catch((err) => {
                // 404 면 우리 DB 에 없음 — apiData 로 화면 채우고 안내. 다른 오류는 그냥 빈 상태.
                if (err?.response?.status === 404 || err?.status === 404) {
                    setDbMissing(true);
                }
                setDbData(null);
            })
            .finally(() => setDbLoading(false));

        // 1-b) 낙찰자 fetch 는 DB 응답 도착 후 bidOpenResult === true 일 때만.
        // (개찰 전이거나 우리 DB 에 row 없는 공고는 호출 자체를 skip — 404/204 노이즈 제거)

        // 2) 외부 API — 백그라운드 (변경 사항 확인)
        setApiLoading(true);
        api.get(`${process.env.REACT_APP_API_BASE_URL}/bids/detail`, { bidNumber })
            .then((d) => {
                setApiData(d);
                setApiLoaded(true);
            })
            .catch((err) => {
                setApiError(err?.message || "외부 API 조회 실패");
                setApiLoaded(true); // 실패도 완료로 표시 (diff 비교는 안 함)
            })
            .finally(() => setApiLoading(false));

        // 3) 유사 공고 — 외부 호출
        setSimilarLoading(true);
        api.get(`${process.env.REACT_APP_API_BASE_URL}/bids/similar_bid`, { bidNumber, bidNoticeOrder })
            .then((data) => setSimilarBid(Array.isArray(data) ? data : (data?.data || [])))
            .catch(() => {})
            .finally(() => setSimilarLoading(false));
    }, [bidNumber, bidNoticeOrder, isOpen]);

    // diff 비교를 위한 통합 데이터 — DB 우선, API 가 추가 필드 보충.
    // dbMissing 인 경우엔 apiData 의 필드를 dbInfo 처럼 펼쳐서 화면이 비어보이지 않게 한다.
    // CATEGORY_FIELDS 의 apiKey 매핑을 활용해 (key → apiKey) 로 값을 옮긴다.
    const dbInfo = useMemo(() => {
        if (dbData) return dbData;
        if (!dbMissing || !apiData) return {};
        const fallback = {};
        Object.values(CATEGORY_FIELDS).flat().forEach((f) => {
            const v = apiData[f.apiKey ?? f.key];
            if (v !== undefined && v !== null) fallback[f.key] = v;
        });
        return fallback;
    }, [dbData, dbMissing, apiData]);
    const apiInfo = useMemo(() => apiData || {}, [apiData]);
    // 변경 감지된 필드 수 — 상단 안내 배너 용
    const diffSummary = useMemo(() => {
        if (!apiLoaded || apiError) return null;
        let newCount = 0, changedCount = 0;
        Object.values(CATEGORY_FIELDS).flat().forEach((f) => {
            const r = computeDiff(dbInfo[f.key], apiInfo[f.apiKey ?? f.key], true);
            if (r.diffState === "new") newCount++;
            else if (r.diffState === "changed") changedCount++;
        });
        return { newCount, changedCount };
    }, [dbInfo, apiInfo, apiLoaded, apiError]);

    // 개찰 이후 여부 — opengDt 가 현재 시각 이전이면 true. 갱신 버튼 노출 조건.
    const openedAlready = useMemo(() => {
        const iso = dbInfo?.opengDt;
        if (!iso) return false;
        const t = dayjs(iso);
        return t.isValid() && t.isBefore(dayjs());
    }, [dbInfo]);

    // 단일 공고 낙찰자 갱신 — 외부 API/크롤링으로 재조회 + DB 반영 후 winner state 갱신.
    const handleRefreshWinner = async () => {
        if (winnerRefreshing) return;
        setWinnerRefreshing(true);
        try {
            const w = await api.post(
                `${process.env.REACT_APP_API_BASE_URL}/bids/${encodeURIComponent(bidNumber)}/${encodeURIComponent(bidNoticeOrder)}/refresh-winner`
            );
            if (w && w.winningCompanyName) {
                setWinner(w);
                notify("낙찰자 정보를 갱신했습니다.", "success");
            } else {
                setWinner(null);
                notify("아직 낙찰자 정보가 확인되지 않습니다.", "info");
            }
            // 낙찰자 외에 전체 투찰 결과도 함께 갱신.
            api.get(`${process.env.REACT_APP_API_BASE_URL}/bids/${encodeURIComponent(bidNumber)}/${encodeURIComponent(bidNoticeOrder)}/bid-results`)
                .then((r) => setBidResults(r && r.opened ? r : null))
                .catch(() => {});
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || "낙찰자 갱신 실패";
            notify(msg, "error");
        } finally {
            setWinnerRefreshing(false);
        }
    };

    // 낙찰 기업 상세로 이동 — 기업 검색 페이지 ?bizNumber= 로.
    const handleGotoWinnerCompany = (bizNumber) => {
        if (!bizNumber) return;
        history.push(`/search/companies/?bizNumber=${encodeURIComponent(bizNumber)}`);
        onClose?.();
    };

    const handleSimilarBidClick = (item) => setSelectModal({ open: true, bid: item });

    const handleGoBidDetail = () => {
        const b = selectModal.bid;
        if (!b) return;
        setSelectedSimilar({ bizNo: b.bizNo, bizOrd: b.bizOrd });
        setSelectModal({ open: false, bid: null });
    };

    const handleGoCompany = () => {
        const bizNumber2 = selectModal.bid?.bidResultWinnerCompanyInfo?.winnerCompanyBizNumber;
        if (bizNumber2) {
            const url = `/search/companies/?bizNumber=${encodeURIComponent(bizNumber2)}`;
            window.open(url, "_blank", "noopener,noreferrer");
        }
        setSelectModal({ open: false, bid: null });
    };

    const handleClose = () => {
        // 모달 닫을 때 진행 중인 AI 분석도 종료. 백엔드는 다음 send() 시 IOException → cancellation.
        if (aiAbortRef.current) {
            try { aiAbortRef.current.abort(); } catch (_) {}
            aiAbortRef.current = null;
        }
        setSelectedSimilar(null);
        setSelectModal({ open: false, bid: null });
        onClose?.();
    };

    /** 첨부 중 PDF 가 1개 이상이면 AI 분석 가능. DB 우선 (외부 API 첨부는 옵션). */
    const pdfCount = useMemo(() => {
        const list = Array.isArray(dbData?.attachments) ? dbData.attachments : [];
        return list.filter(a => (a.fileName || "").toLowerCase().endsWith(".pdf")).length;
    }, [dbData]);

    /** AI 분석 시작 — SSE 로 token 청크를 받아 aiText 에 누적. */
    const runAnalysis = async () => {
        if (!bidNumber || !bidNoticeOrder) return;
        setAiText("");
        setAiPhase("running");
        setAiError(null);
        setAiMessage("요청 전송 중...");

        const ctrl = new AbortController();
        aiAbortRef.current = ctrl;
        const token = localStorage.getItem("auth_token");
        const url = `${process.env.REACT_APP_API_BASE_URL}/bids/${encodeURIComponent(bidNumber)}/${encodeURIComponent(bidNoticeOrder)}/analyze`;
        try {
            const response = await fetch(url, {
                method: "POST",
                signal: ctrl.signal,
                headers: {
                    "Accept": "text/event-stream",
                    ...(token ? { "X-TOKEN": token } : {}),
                },
            });
            if (!response.ok) {
                let msg = "AI 분석 요청 실패";
                try { const j = await response.json(); msg = j?.message || msg; } catch (_) {}
                throw new Error(msg);
            }
            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");
            let buffer = "";
            let currentEvent = "message";
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                let idx;
                while ((idx = buffer.indexOf("\n")) >= 0) {
                    const line = buffer.slice(0, idx).replace(/\r$/, "");
                    buffer = buffer.slice(idx + 1);
                    if (line === "") { currentEvent = "message"; continue; }
                    if (line.startsWith("event:")) {
                        currentEvent = line.slice(6).trim();
                    } else if (line.startsWith("data:")) {
                        const data = line.slice(5).trim();
                        try {
                            const parsed = JSON.parse(data);
                            if (currentEvent === "started") {
                                setAiMessage(`PDF ${parsed.pdfCount}건 분석 시작`);
                            } else if (currentEvent === "progress") {
                                setAiMessage(parsed.message || parsed.stage);
                            } else if (currentEvent === "token") {
                                setAiText(prev => prev + (parsed.text ?? ""));
                            } else if (currentEvent === "done") {
                                setAiPhase("done");
                                setAiMessage(`완료 (PDF ${parsed.succeededPdfs} 성공 / ${parsed.failedPdfs} 실패)`);
                            } else if (currentEvent === "error") {
                                setAiError(parsed);
                                setAiPhase("error");
                                setAiMessage(parsed.message || parsed.code);
                            }
                        } catch (_) {}
                    }
                }
            }
        } catch (err) {
            if (err?.name === "AbortError") {
                setAiPhase("idle");
                return;
            }
            setAiError({ code: "NETWORK_ERROR", message: err.message });
            setAiPhase("error");
            setAiMessage(err.message || "네트워크 오류");
        } finally {
            aiAbortRef.current = null;
        }
    };

    return (
        <>
            <Dialog
                open={isOpen}
                onClose={handleClose}
                fullScreen={isMobile}
                fullWidth
                maxWidth="lg"
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : 2,
                        overflow: "hidden",
                        boxShadow: isMobile ? "none" : "0 18px 60px rgba(15, 23, 42, 0.18)",
                    },
                }}
            >
                <DialogTitle sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${ds.colors.borderLight}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ ...ds.typography.sectionTitle, fontSize: 18, lineHeight: 1.3 }} noWrap>
                                {dbInfo?.bidNtceNm || "입찰 상세정보"}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.3 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                                    {bidNumber}-{bidNoticeOrder}
                                </Typography>
                                {dbInfo?.ntceInsttNm && (
                                    <>
                                        <Typography variant="caption" color="text.secondary">·</Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap>
                                            {dbInfo.ntceInsttNm}
                                        </Typography>
                                    </>
                                )}
                            </Stack>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                            {openedAlready && canBidRefresh && (
                                <Tooltip title="외부 나라장터에서 개찰 결과(낙찰자) 정보를 다시 가져옵니다.">
                                    <span>
                                        <ActionButton
                                            variant="secondary"
                                            startIcon={winnerRefreshing
                                                ? <CircularProgress size={14} />
                                                : <RefreshRoundedIcon fontSize="small" />}
                                            onClick={handleRefreshWinner}
                                            disabled={winnerRefreshing}
                                            label={winnerRefreshing ? "갱신 중..." : "개찰 결과 갱신"}
                                        />
                                    </span>
                                </Tooltip>
                            )}
                            <IconButton onClick={handleClose} aria-label="close">
                                <CloseIcon />
                            </IconButton>
                        </Stack>
                    </Box>
                </DialogTitle>

                {/* ✅ 모달 내부에서만 스크롤되게 */}
                <DialogContent
                    sx={{
                        bgcolor: ds.colors.surface,
                        p: { xs: 2, md: 2.5 },
                        height: isMobile ? "auto" : "calc(100vh - 120px)",
                        overflow: "auto",
                    }}
                >
                    {/* DB 로딩 중에만 backdrop (외부 API 는 백그라운드이라 차단 안 함) */}
                    <Backdrop
                        open={dbLoading}
                        sx={{ zIndex: (t) => t.zIndex.drawer + 1, bgcolor: "rgba(255,255,255,0.85)" }}
                    >
                        <LoadingDots label="입찰 상세 정보를 불러오고 있어요" />
                    </Backdrop>

                    {/* 히어로 — 핵심 정보 카드 (상태 / D-day / 추정가 / 마감). winner 가 있으면 빨간 톤. */}
                    {!dbLoading && dbInfo && (() => {
                        // 낙찰 확정 오버라이드 제거 — 낙찰자 인지 computeStatus 결과를 그대로 표시.
                        // (winner 상태를 주입: detail-from-db 응답엔 winningCompanyName 이 없음)
                        const status = computeStatus(dbInfo, winner?.winningCompanyName);
                        const dd = computeDday(dbInfo.bidClseDt);
                        const presmpt = dbInfo.presmptPrce ? `₩ ${Number(dbInfo.presmptPrce).toLocaleString()}` : "—";
                        const closeAt = dbInfo.bidClseDt
                            ? dayjs(dbInfo.bidClseDt).format("YYYY-MM-DD HH:mm")
                            : "—";
                        return (
                            <Paper variant="outlined" sx={{
                                mb: 1.5, p: 2, borderRadius: 0,
                                bgcolor: ds.colors.surface,
                                borderColor: ds.colors.borderLight,
                            }}>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Chip label={status.label} size="small"
                                              sx={{ bgcolor: status.bg, color: status.color, fontWeight: 800, borderRadius: 0 }} />
                                        {dd && (
                                            <Chip label={dd.label} size="small"
                                                  sx={{ bgcolor: dd.bg, color: "#fff", fontWeight: 800, borderRadius: 0 }} />
                                        )}
                                    </Stack>
                                    <Box sx={{ flex: 1 }} />
                                    <Stack direction="row" spacing={3} alignItems="center" sx={{ fontSize: 13 }}>
                                        <Stack spacing={0.3}>
                                            <Typography variant="caption" color="text.secondary">추정가격</Typography>
                                            <Typography sx={{ fontWeight: 800, color: dbInfo.presmptPrce ? "#dc2626" : "#94a3b8" }}>
                                                {presmpt}
                                            </Typography>
                                        </Stack>
                                        <Stack spacing={0.3}>
                                            <Typography variant="caption" color="text.secondary">입찰마감</Typography>
                                            <Typography sx={{ fontWeight: 700, color: "#2563eb" }}>{closeAt}</Typography>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </Paper>
                        );
                    })()}

                    {/* 전체 투찰 결과 — 개찰완료 공고의 모든 투찰 업체/금액/순위/결과 */}
                    {!dbLoading && bidResults && bidResults.opened && (
                        <Paper variant="outlined" sx={{ mb: 1.5, p: 2, borderRadius: 0, borderColor: "#e2e8f0" }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.2, flexWrap: "wrap", gap: 0.6 }}>
                                <RuleIcon fontSize="small" sx={{ color: "#475569" }} />
                                <Typography sx={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>
                                    전체 투찰 결과
                                </Typography>
                                <Chip size="small" label={`투찰 ${bidResults.bidderCount}개사`}
                                      sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: "#f1f5f9", color: "#334155", borderRadius: 0 }} />
                                {bidResults.soleBid && (
                                    <Chip size="small" label="단독응찰"
                                          sx={{ height: 20, fontSize: 11, fontWeight: 800, bgcolor: "#fef3c7", color: "#b45309", borderRadius: 0 }} />
                                )}
                                {bidResults.resultStatus === "FAILED" && (
                                    <Chip size="small" label="유찰"
                                          sx={{ height: 20, fontSize: 11, fontWeight: 800, bgcolor: "#fee2e2", color: "#b91c1c", borderRadius: 0 }} />
                                )}
                                {bidResults.resultStatus === "UNDETERMINED" && (
                                    <Chip size="small" label="결과 확인필요"
                                          sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: "#f1f5f9", color: "#64748b", borderRadius: 0 }} />
                                )}
                                {bidResults.resultStatus === "NO_RESULT_DATA" && (
                                    <Chip size="small" label="개찰 결과 데이터 없음"
                                          sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: "#f1f5f9", color: "#64748b", borderRadius: 0 }} />
                                )}
                                {/* 낙찰일자 — 낙찰자 카드에서 이곳으로 이동. 헤더 우측 정렬. */}
                                {bidResults.winningDate && (
                                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: "auto" }}>
                                        <CalendarMonthIcon sx={{ fontSize: 14, color: "#b91c1c" }} />
                                        <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#b91c1c", whiteSpace: "nowrap" }}>
                                            낙찰일자 {bidResults.winningDate}
                                        </Typography>
                                    </Stack>
                                )}
                            </Stack>

                            {bidResults.bidderCount === 0 ? (
                                <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>
                                    개찰은 완료됐으나 개찰 결과 데이터가 없습니다 — 유찰 여부는 확인되지 않습니다.
                                </Typography>
                            ) : (
                                <>
                                    {bidResults.resultStatus === "FAILED" && (
                                        <Typography sx={{ fontSize: 11.5, color: "#b91c1c", mb: 0.8, fontWeight: 700 }}>
                                            ※ 개찰결과구분상 ‘유찰’로 확인된 공고입니다.
                                        </Typography>
                                    )}
                                    {bidResults.resultStatus === "UNDETERMINED" && (
                                        <Typography sx={{ fontSize: 11.5, color: "#64748b", mb: 0.8 }}>
                                            ※ 개찰은 됐으나 낙찰자/유찰 근거가 아직 확인되지 않았습니다. 상단 ‘개찰 결과 갱신’으로 재조회할 수 있습니다.
                                        </Typography>
                                    )}
                                    {bidResults.soleBid && bidResults.resultStatus !== "FAILED" && (
                                        <Typography sx={{ fontSize: 11.5, color: "#b45309", mb: 0.8 }}>
                                            ※ 단독응찰입니다. (단독응찰 자체가 유찰은 아니며, 개찰결과구분에 따라 결정됩니다.)
                                        </Typography>
                                    )}
                                    <Box sx={{ overflowX: "auto" }}>
                                        <ResizableTable size="small">
                                            <TableHead>
                                                <TableRow sx={{ "& th": { fontSize: 11.5, fontWeight: 700, color: "#64748b", bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" } }}>
                                                    <TableCell sx={{ width: 60, textAlign: "center" }}>순위</TableCell>
                                                    <TableCell>업체명 (대표)</TableCell>
                                                    <TableCell sx={{ textAlign: "right" }}>투찰금액</TableCell>
                                                    <TableCell sx={{ width: 80, textAlign: "right" }}>투찰률</TableCell>
                                                    <TableCell sx={{ width: 90, textAlign: "center" }}>결과</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {bidResults.bidders.map((b, i) => (
                                                    <TableRow
                                                        key={b.businessNumber ? `${b.businessNumber}-${i}` : i}
                                                        hover={!!b.businessNumber}
                                                        onClick={() => b.businessNumber && handleGotoWinnerCompany(b.businessNumber)}
                                                        sx={{
                                                            cursor: b.businessNumber ? "pointer" : "default",
                                                            bgcolor: b.isWinner ? "#fef2f2" : "transparent",
                                                            "& td": { fontSize: 12, borderBottom: "1px solid #f1f5f9" },
                                                        }}
                                                    >
                                                        <TableCell sx={{ textAlign: "center", fontWeight: 700, color: "#475569" }}>
                                                            {b.rank ?? "—"}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={0.6} alignItems="center" sx={{ flexWrap: "wrap", gap: 0.3 }}>
                                                                {b.isWinner && (
                                                                    <Box component="span" sx={{
                                                                        px: 0.6, py: 0.1, fontSize: 10, fontWeight: 800,
                                                                        color: "#fff", bgcolor: "#dc2626", borderRadius: 0,
                                                                    }}>낙찰</Box>
                                                                )}
                                                                <Typography component="span" sx={{
                                                                    fontWeight: b.isWinner ? 800 : 600,
                                                                    fontSize: 12.5,
                                                                    color: b.isWinner ? "#991b1b" : "#0f172a",
                                                                }}>
                                                                    {b.companyName || "—"}
                                                                </Typography>
                                                                {b.ceoNm && (
                                                                    <Typography component="span" sx={{ fontSize: 11, color: "#94a3b8" }}>
                                                                        ({b.ceoNm})
                                                                    </Typography>
                                                                )}
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell sx={{ textAlign: "right", fontWeight: 700, color: b.isWinner ? "#dc2626" : "#1e293b", whiteSpace: "nowrap" }}>
                                                            {b.bidPrice != null ? `₩ ${Number(b.bidPrice).toLocaleString()}` : "—"}
                                                        </TableCell>
                                                        <TableCell sx={{ textAlign: "right", color: "#475569", whiteSpace: "nowrap" }}>
                                                            {b.bidprcrt ? `${b.bidprcrt}%` : "—"}
                                                        </TableCell>
                                                        <TableCell sx={{
                                                            textAlign: "center",
                                                            fontWeight: (b.failed || b.isWinner) ? 800 : 400,
                                                            color: b.failed ? "#b91c1c" : (b.isWinner ? "#dc2626" : "#64748b"),
                                                        }}>
                                                            {b.opengRsltDivNm || (b.isWinner ? "낙찰" : "—")}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </ResizableTable>
                                    </Box>
                                </>
                            )}
                        </Paper>
                    )}

                    {/* DB 미저장 공고 안내 — 외부 API 응답으로 표시 중 */}
                    {!dbLoading && dbMissing && (
                        <Alert severity="info" icon={false} sx={{
                            mb: 1.5, borderRadius: 0,
                            bgcolor: "#eff6ff", border: "1px solid #bfdbfe",
                            color: "#1e40af",
                        }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                                💡 우리 DB 에 아직 저장되지 않은 공고입니다 — 외부 API 응답으로 임시 표시 중.
                                <Box component="span" sx={{ ml: 0.5, color: "#1e3a8a", fontWeight: 500 }}>
                                    다음 자동 수집(10분 주기) 또는 관리자의 "공고 갱신" 후 우리 DB 에도 반영됩니다.
                                </Box>
                            </Typography>
                        </Alert>
                    )}

                    {/* AI 분석 패널 — PDF 첨부가 있을 때만 표시 */}
                    {!dbLoading && pdfCount > 0 && canBidRefresh && (
                        <Paper variant="outlined" sx={{
                            mb: 1.5, p: 2, borderRadius: 0,
                            borderColor: aiPhase === "error" ? "#fecaca" : "#bfdbfe",
                            bgcolor: aiPhase === "error" ? "#fef2f2" : "#eff6ff",
                        }}>
                            <Stack direction="row" alignItems="center" spacing={1.2}>
                                <AutoAwesomeIcon sx={{ color: "#2563eb" }} />
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
                                        AI 공고문 분석
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        PDF 첨부 {pdfCount}건을 읽어 핵심 요약 · 자격 · 평가 · 일정 · 리스크를 정리합니다.
                                    </Typography>
                                </Box>
                                {aiPhase === "running" ? (
                                    <ActionButton
                                        variant="secondary"
                                        startIcon={false}
                                        onClick={() => aiAbortRef.current?.abort()}
                                        sx={{ borderRadius: 0 }}
                                        label="중단"
                                    />
                                ) : (
                                    <ActionButton
                                        variant="primary"
                                        startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                                        onClick={runAnalysis}
                                        sx={{ borderRadius: 0 }}
                                        label={aiPhase === "done" || aiPhase === "error" ? "다시 분석" : "AI 분석 결과 생성"}
                                    />
                                )}
                            </Stack>

                            {(aiPhase === "running" || aiText || aiPhase === "error") && (
                                <Box sx={{ mt: 1.5 }}>
                                    {aiPhase === "running" && (
                                        <LinearProgress sx={{ borderRadius: 0, height: 4, mb: 1 }} />
                                    )}
                                    {aiMessage && (
                                        <Typography sx={{ fontSize: 11.5, color: ds.colors.textMuted, mb: 0.5 }}>
                                            {aiMessage}
                                        </Typography>
                                    )}
                                    {aiError && (
                                        <Alert severity="error" sx={{ mb: 1, borderRadius: 0 }}>
                                            {aiError.message}
                                            {aiError.code && (
                                                <Typography sx={{ fontSize: 11, opacity: 0.7, mt: 0.3 }}>
                                                    [code: {aiError.code}]
                                                </Typography>
                                            )}
                                        </Alert>
                                    )}
                                    {aiText && (
                                        <Box sx={{
                                            bgcolor: "#fff",
                                            border: `1px solid ${ds.colors.borderLight}`,
                                            p: 1.5,
                                            maxHeight: 460,
                                            overflowY: "auto",
                                            fontSize: 13.5,
                                            lineHeight: 1.55,
                                            "& h1, & h2, & h3": { mt: 1.2, mb: 0.5, fontWeight: 800 },
                                            "& h2": { fontSize: 15, color: "#1e3a8a" },
                                            "& h3": { fontSize: 13.5, color: "#334155" },
                                            "& p":  { my: 0.4 },
                                            "& ul, & ol": { pl: 2.5, my: 0.3 },
                                            "& code": { bgcolor: "#f1f5f9", px: 0.5, fontSize: 12.5 },
                                            "& pre": { bgcolor: "#f8fafc", p: 1, overflowX: "auto", fontSize: 12 },
                                            "& table": { borderCollapse: "collapse", my: 0.5, fontSize: 12.5,
                                                         display: "block", overflowX: "auto", maxWidth: "100%" },
                                            "& th, & td": { border: `1px solid ${ds.colors.borderLight}`, px: 0.6, py: 0.3 },
                                        }}>
                                            <ReactMarkdown
                                                remarkPlugins={[remarkGfm]}
                                                components={aiMarkdownComponents}
                                            >
                                                {aiText}
                                            </ReactMarkdown>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Paper>
                    )}

                    {/* 변경사항 안내 배너 */}
                    {/*{!dbLoading && (*/}
                    {/*    <Box sx={{ mb: 1.5, px: 1.5, py: 1, borderRadius: 0 }}>*/}
                    {/*        <Typography sx={{ fontSize: 12, fontWeight: 600,*/}
                    {/*            color: apiLoading ? "#1e40af" : apiError ? "#991b1b" :*/}
                    {/*                   (diffSummary && (diffSummary.newCount > 0 || diffSummary.changedCount > 0)) ? "#854d0e" : "#166534" }}>*/}
                    {/*            {apiLoading && "🔄 외부 API 에서 최신 데이터를 확인 중..."}*/}
                    {/*            {!apiLoading && apiError && `⚠️ 외부 API 확인 실패 — DB 데이터만 표시 (${apiError})`}*/}
                    {/*            {!apiLoading && !apiError && diffSummary && (diffSummary.newCount > 0 || diffSummary.changedCount > 0) &&*/}
                    {/*                `변경 감지: 신규 ${diffSummary.newCount}개, 변경 ${diffSummary.changedCount}개 — 노란색 강조 표시`}*/}
                    {/*            {!apiLoading && !apiError && diffSummary && diffSummary.newCount === 0 && diffSummary.changedCount === 0 &&*/}
                    {/*                "DB 데이터가 외부 API 최신 상태와 일치"}*/}
                    {/*        </Typography>*/}
                    {/*    </Box>*/}
                    {/*)}*/}

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 420px" },
                            gap: 2.5,
                            alignItems: "start",
                        }}
                    >
                        {/* ✅ 좌측: 카드 세로형(한 줄에 한 카드) */}
                        <Stack spacing={2} sx={{ minWidth: 0 }}>
                            {/* 진행 일정 타임라인 — 산업의역군 스타일 수직 타임라인. 히어로 바로 다음 */}
                            <ScheduleTimelineCard dbInfo={dbInfo} />

                            {Object.entries(CATEGORY_FIELDS).map(([title, fields]) => (
                                <CategoryCard key={title} title={title} fields={fields}
                                    dbData={dbInfo} apiData={apiInfo} apiLoaded={apiLoaded && !apiError} />
                            ))}

                            {/* 첨부파일 — DB 우선, API 차이가 있으면 신규/변경 강조 */}
                            <AttachmentsCard dbInfo={dbInfo} apiInfo={apiInfo} apiLoaded={apiLoaded && !apiError} />
                        </Stack>

                        {/* ✅ 우측: 유사공고는 데스크톱에서 sticky */}
                        <Box
                            sx={{
                                width: "100%",
                                position: { xs: "static", md: "sticky" },
                                top: { md: 16 },
                                alignSelf: "start",
                            }}
                        >
                            <SimilarBidCardList
                                data={similarBid}
                                loading={similarBidLoading}
                                onClickBid={handleSimilarBidClick}
                            />
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            <SelectMoveDialog
                open={selectModal.open}
                onClose={() => setSelectModal({ open: false, bid: null })}
                bid={selectModal.bid}
                onBid={handleGoBidDetail}
                onCompany={handleGoCompany}
            />

            {/* 재귀 오픈 유지 */}
            {selectedSimilar && (
                <BidDetailModal
                    isOpen={true}
                    onClose={() => setSelectedSimilar(null)}
                    bidNumber={selectedSimilar.bizNo}
                    bidNoticeOrder={selectedSimilar.bizOrd}
                />
            )}
        </>
    );
};

export default BidDetailModal;
